// src/contexts/SubscriptionContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { subscriptionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();

  const FREE_TRIAL_DAYS = 30;

  const loadSubscriptionData = useCallback(async () => {
    try {
      setLoading(true);

      if (!token) {
        setSubscription(null);
        setPlans([]);
        return;
      }

      // Fetch subscription and plans in parallel
      const [subscriptionResponse, plansResponse] = await Promise.allSettled([
        subscriptionAPI.getSubscription(),
        subscriptionAPI.getPlans()
      ]);

      // Handle subscription response
      if (subscriptionResponse.status === 'fulfilled' && subscriptionResponse.value) {
        setSubscription(subscriptionResponse.value);
      } else {
        setSubscription(null);
      }

      // Handle plans response
      if (plansResponse.status === 'fulfilled' && plansResponse.value) {
        setPlans(plansResponse.value);
      } else {
        setPlans([]);
      }

    } catch (error) {
      setSubscription(null);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && user) {
      loadSubscriptionData();
    } else {
      setLoading(false);
    }
  }, [token, user, loadSubscriptionData]);

  const checkTrialFromUserData = useCallback(() => {
    if (!user) return false;

    const trialEndDate = user.trial_end_date || user.restaurant_trial_end;
    const isTrialUsed = user.is_trial_used;

    if (!trialEndDate || !isTrialUsed) return false;

    const trialEnd = new Date(trialEndDate);
    const now = new Date();
    return now < trialEnd;
  }, [user]);

  const isTrialActive = useCallback(() => {
    // Check subscription API response first
    if (subscription?.is_trial_active === true) {
      return true;
    }

    // Fallback to user data
    return checkTrialFromUserData();
  }, [subscription, checkTrialFromUserData]);

  const getTrialDaysRemaining = useCallback(() => {
    // Priority 1: Subscription API data
    if (subscription?.trial_days_remaining !== undefined) {
      return subscription.trial_days_remaining;
    }

    // Priority 2: User trial end date
    if (user?.trial_end_date) {
      const trialEnd = new Date(user.trial_end_date);
      const now = new Date();
      const diffDays = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    }

    return 0;
  }, [subscription, user]);

  const getCurrentPlan = useCallback(() => {
    // TRIAL FIRST - if trial is active, return 'pro'
    if (isTrialActive()) {
      return 'pro';
    }

    // Check the RESTAURANT's subscription_plan from user data
    if (user?.subscription_plan && user.subscription_plan !== 'free') {
      return user.subscription_plan;
    }

    // Fallback to subscription API data
    if (subscription?.plan) {
      return subscription.plan;
    }

    if (subscription?.restaurant_plan) {
      return subscription.restaurant_plan;
    }

    // Default to free
    return 'free';
  }, [isTrialActive, subscription, user]);

  const isSubscriptionActive = useCallback(() => {
    if (!user) return false;

    // Check trial first
    if (isTrialActive()) {
      return true;
    }

    // Check subscription API data
    if (subscription) {
      const subData = subscription.data || subscription;

      if (subData.is_active) return true;
      if (subData.subscription?.status === 'active' || subData.subscription?.status === 'trialing') {
        return true;
      }
    }

    // Check user data
    if (user.subscription_plan === 'pro') {
      return true;
    }

    return false;
  }, [user, subscription, isTrialActive]);

  const PLAN_LIMITS = useMemo(() => ({
    free: {
      max_staff: 3,
      max_orders: 50,
      max_tables: 5,
      features: ['basic_menu', 'email_support']
    },
    pro: {
      max_staff: -1,
      max_orders: -1,
      max_tables: -1,
      features: ['advanced_menu', 'priority_support', 'analytics', 'custom_domain']
    }
  }), []);

  const getPlanLimits = useCallback((plan) => {
    return PLAN_LIMITS[plan] || PLAN_LIMITS.free;
  }, [PLAN_LIMITS]);

  const isWithinLimits = useCallback((type, current) => {
    const currentPlan = getCurrentPlan();
    const limits = getPlanLimits(currentPlan);
    const limit = limits[`max_${type}`];

    // Trial users have unlimited access
    if (isTrialActive()) {
      return true;
    }

    if (limit === -1) return true;
    return current < limit;
  }, [getCurrentPlan, getPlanLimits, isTrialActive]);

  const isFeatureAvailable = useCallback((feature) => {
    if (isTrialActive()) return true;

    const currentPlan = getCurrentPlan();
    const limits = getPlanLimits(currentPlan);
    return limits.features?.includes(feature) || false;
  }, [isTrialActive, getCurrentPlan, getPlanLimits]);

  const canUpgrade = useCallback(() => {
    const currentPlan = getCurrentPlan();
    return currentPlan !== 'enterprise';
  }, [getCurrentPlan]);

  const getUpgradeMessage = useCallback((feature) => {
    if (isTrialActive()) {
      const daysRemaining = getTrialDaysRemaining();
      return `Your free trial ends in ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}. Upgrade to keep access to this feature.`;
    }

    const currentPlan = getCurrentPlan();

    switch (currentPlan) {
      case 'free':
        return 'Upgrade to Professional to access this feature';
      case 'pro':
        return 'Upgrade to Enterprise for unlimited access';
      default:
        return 'Contact support for more information';
    }
  }, [isTrialActive, getTrialDaysRemaining, getCurrentPlan]);

  const refreshSubscription = useCallback(async () => {
    await loadSubscriptionData();

    setTimeout(() => {
      toast.success('Subscription data refreshed');
    }, 500);
  }, [loadSubscriptionData]);

  const value = useMemo(() => ({
    subscription,
    plans,
    loading,
    loadSubscriptionData,
    getCurrentPlan,
    isFeatureAvailable,
    isWithinLimits,
    canUpgrade,
    getUpgradeMessage,
    isTrialActive,
    getTrialDaysRemaining,
    isSubscriptionActive,
    refreshSubscription,
    FREE_TRIAL_DAYS
  }), [
    subscription,
    plans,
    loading,
    loadSubscriptionData,
    getCurrentPlan,
    isFeatureAvailable,
    isWithinLimits,
    canUpgrade,
    getUpgradeMessage,
    isTrialActive,
    getTrialDaysRemaining,
    isSubscriptionActive,
    refreshSubscription
  ]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};